<template>
  <tbody class="do-tbody">
  <tr class="calendar-header">
    <td>
      <div class="calendar-header__title">
        <DDropdown>
          <ul slot="list">
            <li @click="$emit('createSubject', {calendar, users})">
              Tilføj person til stue
            </li>
          </ul>
        </DDropdown>
        {{ calendar.name }}
      </div>
    </td>
    <td class="norm">Norm</td>
    <td class="norm">I ugen</td>
    <td v-for="day in eventsInDays" :key="day.dateId">
      <div class="calendar__header__day">
        <div>
          <DutySchedulePlanCalendarDayStats
            :openingHours="openingHours"
            :hours="day.v_formattedTime">
          </DutySchedulePlanCalendarDayStats>
        </div>
      </div>
    </td>
  </tr>
  <tr v-for="user in usersWithCalc" :key="`user-row-${user.id}`" class="user-row">
    <td>
      <div class="user-name">
        <DDropdown @open="readUserSubject(user)" class="title--grey">
          <ul slot="list">
            <li v-if="isLoadingTheUserSubject">
              <div class="do-spinner"></div>
            </li>
            <li class="d-item-warning" @click="setTheDeleteUser(user)" v-else>
              <span class="icon-bin"></span> {{ theUserSubject && theUserSubject.can_delete ? 'Slet' : 'Fjern' }} fra
              stue
            </li>
          </ul>
        </DDropdown>
        {{ user.name }}
      </div>
    </td>
    <td class="norm">{{ user.v_employment }}</td>
    <td class="norm">{{ user.v_events.v_minutesFormatted }}</td>
    <td v-for="day in days" :key="`day-cell-${user.id}-${day.dateFormatted}`">
      <DutyScheduleCalendarUserDay
        :user="user"
        :date="day.date"
        :eventsInHash="eventsInHash"
        :subjectsInUserCalIdHash="subjectsInUserCalIdHash"
        :theCreateEventObj="theCreateEventObj"
        @createEvent="$emit('createEvent', $event)"
        @editEvent="$emit('editEvent', $event)">
      </DutyScheduleCalendarUserDay>
    </td>
  </tr>
  <ModalConfirmation
    v-if="theDeleteUser"
    @close="setTheDeleteUser(null)"
    @confirm="deleteUserSubject"
    :showClose="false"
    :isSubmitButtonLoading="isDeletingUserSubject">
  </ModalConfirmation>
  </tbody>
</template>

<script>
import docendo from '../../docendo'
import moment from 'moment'
import DutyScheduleCalendarUserDay from './DutySchedulePlanCalendarUserDay.vue'
import DDropdown from '../common/DDropdown.vue'
import ModalConfirmation from '../Modals/ModalConfirmation.vue'
import DutySchedulePlanCalendarDayStats from './DutySchedulePlanCalendarDayStats.vue'

export default {
  components: {DutySchedulePlanCalendarDayStats, ModalConfirmation, DDropdown, DutyScheduleCalendarUserDay},
  props: {
    calendar: {
      type: Object,
      default: null
    },
    date: {
      default: () => new Date()
    },
    usersCalIdsHash: {
      type: Object
    },
    days: {
      type: Array,
      required: true
    },
    colspan: {
      type: Number,
      required: false,
      default: 6 // Standardværdi for colspan
    },
    events: {
      type: Array,
      default: () => []
    },
    eventsInHash: {
      default: {}
    },
    theCreateEventObj: {
      type: Object,
      default: null
    },
    userNorms: {
      type: Array,
      default: []
    },
    theNewlyCreatedSubject: {
      type: Object,
      default: null
    },
    schoolSettings: {
      type: Object,
      default: null
    },
    openingHours: {
      default: null
    }
  },
  data () {
    return {
      subjects: [],
      isLoadingSubjects: false,
      subjectsInUserCalIdHash: {}, // {13: event, .. }
      users: [],
      isCreateSubject: false,
      theDeleteUser: null,
      isDeletingUserSubject: false,
      isLoadingTheUserSubject: false,
      theUserSubject: null
    }
  },
  computed: {
    schoolSettingsColor () {
      return this.schoolSettings && this.schoolSettings
    },
    usersSorted () {
      return this.users.sort((a, b) => a.name.localeCompare(b.name))
    },
    usersWithCalc () {
      const users = this.usersSorted.map(user => ({
        ...user,
        v_events: this.getEventsForCalendarId(user.user_calendar_id),
        v_employment: this.getEmployment(user)
      }))

      return users
    },
    eventsInDays () {
      const days = this.days.map(day => ({
        ...day,
        v_minutes: 0
      }))

      this.events.forEach(event => {
        event.calendars.forEach(calEvent => {
          if (calEvent.calendar_id === this.calendar.id) {
            const eventDate = moment(event.start).format('YYYY-MM-DD')
            const idx = days.findIndex(day => day.dateId === eventDate)
            if (idx >= 0) {
              days[idx].v_minutes += calEvent.minutes
            }
          }
        })
      })
      return days.map(day => ({
        ...day,
        v_formattedTime: `${Math.floor(day.v_minutes / 60)}t ${day.v_minutes % 60}m`
      }))
    }
  },
  watch: {
    theNewlyCreatedSubject (newSubject) {
      if (newSubject.calendar_id === this.calendar.id) {
        this.subjects.push(newSubject)
        this.matchSubjects()
      }
    }
  },
  methods: {
    setTheDeleteUser (user) {
      this.theDeleteUser = user
    },
    createSubject () {
      this.isCreateSubject = true
    },
    getEmployment (user) {
      const userNorm = this.userNorms.find(userNorm => userNorm.user_id === user.id)
      const employmentPerWeek = userNorm && userNorm.norm ? userNorm.norm.find(normObj => normObj.title === 'Timeansættelse pr. uge') : null
      return employmentPerWeek ? employmentPerWeek.value : null
    },
    getEventsForCalendarId (calendarId) {
      const obj = {
        v_events: [],
        v_minutes: 0,
        v_minutesFormatted: ''
      }

      this.events.forEach(event => {
        event.calendars.forEach(calEvent => {
          if (calEvent.calendar_id === calendarId) {
            obj.v_events.push(event)
            obj.v_minutes += calEvent.minutes
          }
        })
      })
      // Beregn og tilføj formateret tid
      const hours = Math.floor(obj.v_minutes / 60)
      const minutes = obj.v_minutes % 60
      obj.v_minutesFormatted = `${hours}t ${minutes}m`

      return obj
    },
    readSubjects () {
      this.isLoadingSubjects = true
      docendo.sidebar(this.calendar.id, this.date, success => {
        this.isLoadingSubjects = false
        this.subjects = success
        this.matchSubjects()
      }, failure => {
        console.log('something went wrong')
      })
    },
    matchSubjects () {
      this.users = []
      this.subjects.forEach(subject => {
        subject.cals.forEach(calEvent => {
          if (calEvent.calendar_type === 'user') {
            const userCalendarId = calEvent.id
            const user = this.usersCalIdsHash[userCalendarId]
            if (user) {
              // const idx = this.users.findIndex(user => user.id === calEvent.id)
              this.users.push(user)
              this.$set(this.subjectsInUserCalIdHash, userCalendarId, subject)
            }
          }
        })
      })
    },
    resetDeleteUser () {
      this.theDeleteUser = null
      this.isDeletingUserSubject = false
      this.theUserSubject = null
      this.isLoadingTheUserSubject = false
    },
    readUserSubject (user) {
      this.isLoadingTheUserSubject = true
      const subject = this.subjectsInUserCalIdHash[user.user_calendar_id]
      const subjectId = subject.id
      docendo.showSubject(
        subjectId, dbSuccess => {
          if (dbSuccess.id) {
            this.theUserSubject = dbSuccess
          } else {
            window.alert('Personen kan ikke findes')
            this.resetDeleteUser()
          }
          this.isLoadingTheUserSubject = false
        },
        failure => {
          console.error('Error showing subject:', failure)
        }
      )
    },
    deleteUserSubject () {
      if (!this.theUserSubject) {
        console.error('No user selected for deletion')
        return
      }
      this.isDeletingUserSubject = true
      const subject = this.theUserSubject
      //
      if (!subject) {
        console.error('Subject not found for user_calendar_id:')
        this.resetDeleteUser()
        return
      }
      this.deleteSubject(subject)
    },
    deleteSubject (deleteSubject) {
      const subjectId = deleteSubject.id
      const schoolId = deleteSubject.school_id
      const schoolYearId = deleteSubject.school_year_id
      if (deleteSubject.can_delete) {
        docendo.deleteSubject(subjectId, schoolId, schoolYearId, success => {
          if (success.id) {
            const idx = this.subjects.findIndex(subject => subject.id === success.id)
            this.subjects.splice(idx, idx)
            this.matchSubjects()
            this.resetDeleteUser()
          }
        }, failure => {
          console.log('totally failed!')
        })
      } else {
        const userCalendarId = this.theDeleteUser.user_calendar_id
        const calendarIds = deleteSubject.calendar_ids.filter(calId => calId !== userCalendarId)
        docendo.updateSubject(subjectId, deleteSubject.name, deleteSubject.color, deleteSubject.calendar_id, calendarIds, deleteSubject.default_duration, deleteSubject.preparation, deleteSubject.hours, deleteSubject.subject_type, deleteSubject.school_id, schoolYearId, deleteSubject.subject_code, false, false, success => {
          this.readSubjects()
        }, failure => {
          console.log('FAILED!')
        })
        this.resetDeleteUser()
      }
    }
  },
  mounted () {
    this.readSubjects()
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.calendar-header {
  background: var(--doGrey) !important;
  border: 1px solid var(--doBorder);
  font-size: 1rem;
  font-weight: bold;
  color: #728195;
}

.do-tbody tr td {
  height: 1px;
  border: 1px solid #e2e8f0;
}

td.norm {
  padding: 0.25rem 0;
  width: 50px;
  text-align: center;
}

.calendar-header td.norm {
  color: #728195;
  font-weight: normal;
}

.user-name {
  display: flex;
  align-items: center;
  gap: 0rem;
}

.calendar-header__title {
  display: flex;
  align-items: center;
}

.do-bttn--small {
  font-size: 10px;
  height: 22px;
  padding: 0 0.5rem;
  line-height: 20px;
}

.calendar__header__day {
  display: flex;
  justify-content: flex-end;
  padding: 0 0.5rem;
}

</style>



// WEBPACK FOOTER //
// DutySchedulePlanCalendar.vue?25f7e530